<script>
import { validationMixin } from 'vuelidate'
import { required, email } from 'vuelidate/lib/validators'
import { rules } from '@/utils/validators'

import { IS_EMAIL_AVAILABLE_QUERY } from '@/graph/gql/user.gql'

export default {
  name: 'OnboardingProfileBasic',
  mixins: [validationMixin],
  apollo: {
    isEmailAvailable: {
      query: IS_EMAIL_AVAILABLE_QUERY,
      variables() {
        return { email: this.form.email }
      },
      skip() {
        return this.$v.form.email.$invalid
      }
    }
  },
  props: {
    user: { type: Object, required: true },
    submitting: { type: Boolean, default: () => false }
  },
  data() {
    return {
      rules,
      form: {
        firstName: '',
        lastName: '',
        email: ''
      }
    }
  },
  created() {
    // Event tracking
    const { once } = this.lodash
    const { logEvent } = this.$amplitude

    const firstNameAdded = once(() => {
      const props = { firstName: this.form.firstName }
      logEvent('Seller Onboarding: First Name Added', props)
    })
    this.firstNameAdded = () => {
      if (this.form.firstName) firstNameAdded()
    }

    const lastNameAdded = once(() => {
      const props = { lastName: this.form.lastName }
      logEvent('Seller Onboarding: Last Name Added', props)
    })
    this.lastNameAdded = () => {
      if (this.form.lastName) lastNameAdded()
    }

    const emailAdded = once(() => {
      const props = { email: this.form.email }
      logEvent('Seller Onboarding: Email Added', props)
    })
    this.emailAdded = () => {
      if (this.form.email) emailAdded()
    }

    // Prefill form
    const { firstName, lastName, email } = this.user
    this.form = { firstName, lastName, email }
  },
  methods: {
    submit() {
      if (this.submitting || this.$v.$invalid) return
      if (this.$apollo.loading || !this.isEmailAvailable) return

      const data = this.form
      this.$emit('submit', data)
    }
  },
  validations: {
    form: {
      firstName: { required },
      lastName: { required },
      email: { required, email }
    }
  }
}
</script>

<template>
  <div class="view-grid">
    <!-- Onboarding Profile // Basic -->
    <div>
      <form @submit.prevent="submit()">
        <!-- Title -->
        <div class="display-1 font-weight-bold accent--text">
          First a few basics...
        </div>

        <!-- Subtitle -->
        <div class="title primary--text mt-3">
          We'll use this information to keep you updated on your account status
          and upcoming orders.
        </div>

        <!-- Name -->
        <div class="my-3">
          <div class="font-weight-bold">
            How should we call you?
          </div>

          <v-text-field
            v-model.trim="form.firstName"
            :rules="[rules.required]"
            validate-on-blur
            class="secondary--text"
            label="First name"
            @blur="firstNameAdded"
          />

          <v-text-field
            v-model.trim="form.lastName"
            :rules="[rules.required]"
            validate-on-blur
            class="secondary--text"
            label="Last name"
            @blur="lastNameAdded"
          />
        </div>

        <!-- Email -->
        <div class="my-3">
          <div class="font-weight-bold">
            What's your email?
          </div>
          <div class="body-2">
            We'll use it to send you important information about your account
            and orders.
          </div>
          <v-text-field
            v-model.trim="form.email"
            :rules="[rules.required, rules.email]"
            :error-messages="
              $v.form.email.$invalid || $apollo.loading || isEmailAvailable
                ? []
                : ['This Email is already in use']
            "
            validate-on-blur
            class="secondary--text"
            label="Email"
            @blur="emailAdded"
          />
        </div>

        <!-- Action Buttons -->
        <div class="mt-5">
          <v-btn
            :loading="submitting || $apollo.loading"
            :disabled="$v.$invalid || $apollo.loading || !isEmailAvailable"
            type="submit"
            rounded
            depressed
            color="primary"
          >
            Continue
          </v-btn>
        </div>
      </form>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.inputfile
  width: 0.1px
  height: 0.1px
  opacity: 0
  overflow: hidden
  position: absolute
  z-index: -1
  &::-webkit-file-upload-button
    visibility: hidden

.inputfile-label
  display: flex
  justify-content: center
  border: 1px solid #bbb
  border-radius: 50%
  width: 52px
  height: 52px
  overflow: hidden
  img
    width: 100%
    height: 100%
</style>
